import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import type {
    AxisConfiguration,
    AxisPreviewItem,
    ShowTopStats,
} from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import { extractShowTopStatsFromKpis } from 'components/pages/PackArchitecture/packArchitectureChart.helpers';
import {
    buildWhitespaceAxisPreviewRequest,
    type WhitespaceAxisPreviewKind,
} from 'components/pages/WhitespaceAnalysis/whitespaceGridRequest.helpers';
import type { PpaChartKpi } from 'components/pages/PackArchitecture/pricePackArchitecture.types';
import { WHITESPACE_AXIS_PREVIEW_QUERY_OPTIONS } from 'hooks/whitespaceAxisPreviewQuery.helpers';
import { ppaUrls } from 'config/urls';

const SHOW_TOP_PREVIEW_DEBOUNCE_MS = 400;

type PreviewFetchSnapshot = Pick<
    AxisConfiguration,
    'sortBy' | 'sortOrder' | 'showTop' | 'groupRestAsOther' | 'showOwnVendorFirst'
>;

type WhitespaceColumnGroup = {
    ex_id?: string;
    description?: string;
    total_count?: number;
    data?: Array<{
        id?: string;
        data?: Array<{
            kpis?: Array<{ kpi_id?: string; raw_value?: number | null }>;
        }>;
    }>;
};

type WhitespaceGridPreviewKpi = {
    kpi_id?: string;
    raw_value?: number | null;
    value?: string | number | boolean;
};

type WhitespaceGridPreviewResponse = {
    data?: WhitespaceColumnGroup[];
    total_count?: number;
    kpis?: WhitespaceGridPreviewKpi[];
};

type WhitespaceAxisPreviewQueryResult = {
    previewItems: AxisPreviewItem[];
    showTopStats: ShowTopStats | null;
    totalCount: number;
};

const extractWhitespaceAxisTotalCount = (
    response: WhitespaceGridPreviewResponse,
    axisKind: WhitespaceAxisPreviewKind,
    fallback = 0,
): number => {
    // `column` (X-axis) total lives at the top level; `row` (Y-axis
    // attribute-bucket, e.g. Pack_Range) total is per-segment - all segments
    // share the same bucket set, so the first one is representative.
    const raw = axisKind === 'row' ? response.data?.[0]?.total_count : response.total_count;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
        return raw;
    }
    return fallback;
};

const toFetchSnapshot = (config: AxisConfiguration): PreviewFetchSnapshot => ({
    sortBy: config.sortBy,
    sortOrder: config.sortOrder,
    showTop: config.showTop,
    groupRestAsOther: config.groupRestAsOther,
    showOwnVendorFirst: config.showOwnVendorFirst,
});

const hasNonShowTopChange = (
    previous: PreviewFetchSnapshot | null,
    current: PreviewFetchSnapshot,
) =>
    !previous ||
    previous.sortBy !== current.sortBy ||
    previous.sortOrder !== current.sortOrder ||
    previous.groupRestAsOther !== current.groupRestAsOther ||
    previous.showOwnVendorFirst !== current.showOwnVendorFirst;

const extractKpiTotal = (group: WhitespaceColumnGroup, kpiIds: string[]) => {
    for (const bucket of group.data ?? []) {
        for (const record of bucket.data ?? []) {
            for (const kpi of record.kpis ?? []) {
                const normalized = (kpi.kpi_id ?? '').trim().toLowerCase();
                if (kpiIds.includes(normalized) && typeof kpi.raw_value === 'number') {
                    return kpi.raw_value;
                }
            }
        }
    }
    return 0;
};

const buildWhitespaceColumnPreviewItemsFromGrid = (response: WhitespaceGridPreviewResponse): AxisPreviewItem[] =>
    (response.data ?? []).map((group) => ({
        name: group.description ?? group.ex_id ?? '',
        valueSales: extractKpiTotal(group, ['total_value', 'value_sales', 'ps_value_sales']),
        volumeSales: extractKpiTotal(group, ['total_eq_volume', 'volume_sales', 'ps_volume_sales']),
    }));

/** Row (Y-axis attribute-bucket, e.g. Pack_Range) preview items - one per bucket, taken from the first segment (buckets are shared/global, not per-segment). */
const buildWhitespaceRowPreviewItemsFromGrid = (response: WhitespaceGridPreviewResponse): AxisPreviewItem[] =>
    (response.data?.[0]?.data ?? []).map((bucket) => ({
        name: bucket.id ?? '',
        valueSales: extractKpiTotal({ data: [bucket] }, ['total_value', 'value_sales', 'ps_value_sales']),
        volumeSales: extractKpiTotal({ data: [bucket] }, ['total_eq_volume', 'volume_sales', 'ps_volume_sales']),
    }));

const buildWhitespacePreviewItemsFromGrid = (
    response: WhitespaceGridPreviewResponse,
    axisKind: WhitespaceAxisPreviewKind,
): AxisPreviewItem[] =>
    axisKind === 'row'
        ? buildWhitespaceRowPreviewItemsFromGrid(response)
        : buildWhitespaceColumnPreviewItemsFromGrid(response);

const SHARE_KPI_IDS = new Set(['value_sales_share', 'volume_sales_share']);

const hasShareKpis = (kpis: WhitespaceGridPreviewKpi[] | undefined) =>
    (kpis ?? []).some((kpi) => SHARE_KPI_IDS.has((kpi.kpi_id ?? '').trim().toLowerCase()));

const findShareKpisInWhitespaceGrid = (
    response: WhitespaceGridPreviewResponse,
): WhitespaceGridPreviewKpi[] | null => {
    for (const group of response.data ?? []) {
        for (const bucket of group.data ?? []) {
            for (const record of bucket.data ?? []) {
                if (hasShareKpis(record.kpis)) {
                    return record.kpis ?? null;
                }
            }
        }
    }
    return null;
};

const extractWhitespaceShowTopStats = (
    response: WhitespaceGridPreviewResponse,
): ShowTopStats | null => {
    const fromTopLevel = extractShowTopStatsFromKpis(response.kpis as PpaChartKpi[] | undefined);
    if (fromTopLevel) return fromTopLevel;

    const nestedShareKpis = findShareKpisInWhitespaceGrid(response);
    return extractShowTopStatsFromKpis(nestedShareKpis as PpaChartKpi[] | undefined);
};

type UseWhitespaceAxisConfigurationPreviewParams = {
    open: boolean;
    gridRequest: Record<string, unknown> | null;
    previewConfig: AxisConfiguration;
    pauseFetch?: boolean;
    /** `column` for X-axis dimensions (default); `row` for Y-axis attribute-bucket dimensions (e.g. Pack_Range). */
    axisKind?: WhitespaceAxisPreviewKind;
};

export const useWhitespaceAxisConfigurationPreview = ({
    open,
    gridRequest,
    previewConfig,
    pauseFetch = false,
    axisKind = 'column',
}: UseWhitespaceAxisConfigurationPreviewParams) => {
    const [debouncedConfig, setDebouncedConfig] = useState(previewConfig);
    const previousSnapshotRef = useRef<PreviewFetchSnapshot | null>(null);

    useEffect(() => {
        if (!open) {
            previousSnapshotRef.current = null;
            return undefined;
        }

        const currentSnapshot = toFetchSnapshot(previewConfig);
        const fetchDelay = hasNonShowTopChange(previousSnapshotRef.current, currentSnapshot)
            ? 0
            : SHOW_TOP_PREVIEW_DEBOUNCE_MS;
        previousSnapshotRef.current = currentSnapshot;

        const timer = window.setTimeout(() => {
            setDebouncedConfig(previewConfig);
        }, fetchDelay);

        return () => {
            window.clearTimeout(timer);
        };
    }, [open, previewConfig]);

    const previewRequest = useMemo(() => {
        if (!gridRequest || !open) {
            return null;
        }
        return buildWhitespaceAxisPreviewRequest(gridRequest, debouncedConfig, axisKind);
    }, [axisKind, debouncedConfig, gridRequest, open]);

    const previewRequestKey = previewRequest ? JSON.stringify(previewRequest) : null;
    const queryEnabled = open && Boolean(previewRequestKey) && !pauseFetch;

    const { data, isFetching, isPending } = useQuery<WhitespaceAxisPreviewQueryResult>({
        queryKey: ['whitespace-axis-preview', previewRequestKey],
        queryFn: async ({ signal }) => {
            const response = await axios.post<WhitespaceGridPreviewResponse>(
                ppaUrls.getWhitespaceAnalysisGridUrl,
                previewRequest,
                { signal },
            );
            return {
                previewItems: buildWhitespacePreviewItemsFromGrid(response.data, axisKind),
                showTopStats: axisKind === 'row' ? null : extractWhitespaceShowTopStats(response.data),
                totalCount: extractWhitespaceAxisTotalCount(response.data, axisKind),
            };
        },
        enabled: queryEnabled,
        ...WHITESPACE_AXIS_PREVIEW_QUERY_OPTIONS,
    });

    const isConfigDebouncing =
        open &&
        JSON.stringify(toFetchSnapshot(previewConfig)) !== JSON.stringify(toFetchSnapshot(debouncedConfig));

    const isLoading = queryEnabled && (isPending || isFetching || isConfigDebouncing);

    return {
        previewItems: open ? (data?.previewItems ?? []) : [],
        showTopStats: open ? (data?.showTopStats ?? null) : null,
        totalCount: open ? (data?.totalCount ?? 0) : 0,
        isLoading,
    };
};
